// Persistent shell rendered by AdminShell. The sidebar mounts once per
// station entry and stays mounted across tab clicks (the previous
// architecture re-mounted on every navigation because each page wrapped
// the layout itself).
//
// Active highlight is derived from the URL via NavLink's isActive; pages
// no longer need to pass an `active` prop.

import React from 'react';
import { NavLink } from 'react-router-dom';
import { AlertTriangle, CreditCard, Handshake, HelpCircle, Inbox, LayoutDashboard, Megaphone, UsersRound, Tag, LogOut, Settings, Sparkles } from 'lucide-react';
import type { PublisherConfig } from '../../../types/selfService';
import { useAuth } from '../auth/useAuth';
import { displayedName } from '../../../data/publisherConfigs';
import MarketplaceLogo from '../MarketplaceLogo';
import StationSwitcher from './StationSwitcher';
import { useAdminHelpUnread } from '../help/useAdminHelpUnread';

interface PublisherAdminLayoutProps {
  config: PublisherConfig;
  children: React.ReactNode;
}

const navLinkClass = (isActive: boolean) =>
  `flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
    isActive ? 'bg-slate-900 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
  }`;

const PublisherAdminLayout: React.FC<PublisherAdminLayoutProps> = ({ config, children }) => {
  const { user, signOut } = useAuth();
  const isSuperAdmin = user?.rawRole === 'super_admin';
  const { count: adminHelpUnread } = useAdminHelpUnread(isSuperAdmin);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar is `sticky top-0 h-screen` so the Sign out block at the
          bottom stays pinned to the viewport regardless of main-content
          scroll. Overflow lives on the nav so a long menu can scroll
          independently of the header + sign-out footer. */}
      <aside className="w-60 bg-slate-950 text-white flex flex-col sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-slate-800">
          <MarketplaceLogo variant="light" className="mb-3" />
          <div className="text-xs uppercase tracking-wider text-slate-400">Publisher Admin</div>
          <StationSwitcher currentSlug={config.slug} currentName={displayedName(config)} />
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <NavLink to={`/admin/${config.slug}`} end className={({ isActive }) => navLinkClass(isActive)}>
            <LayoutDashboard className="w-4 h-4" />
            Overview
          </NavLink>
          <NavLink to={`/admin/${config.slug}/submissions`} className={({ isActive }) => navLinkClass(isActive)}>
            <Inbox className="w-4 h-4" />
            Submissions
          </NavLink>
          <NavLink to={`/admin/${config.slug}/advertisers`} className={({ isActive }) => navLinkClass(isActive)}>
            <Megaphone className="w-4 h-4" />
            Advertisers
          </NavLink>
          <NavLink to={`/admin/${config.slug}/team`} className={({ isActive }) => navLinkClass(isActive)}>
            <UsersRound className="w-4 h-4" />
            Team
          </NavLink>
          <NavLink to={`/admin/${config.slug}/referrals`} className={({ isActive }) => navLinkClass(isActive)}>
            <Handshake className="w-4 h-4" />
            Referrals
          </NavLink>
          <NavLink to={`/admin/${config.slug}/promotions`} className={({ isActive }) => navLinkClass(isActive)}>
            <Tag className="w-4 h-4" />
            Promotions
          </NavLink>
          <NavLink to={`/admin/${config.slug}/payments`} className={({ isActive }) => navLinkClass(isActive)}>
            <CreditCard className="w-4 h-4" />
            Payments (Stripe)
          </NavLink>
          {/* Super_admin-only — channel CRUD lives inside the per-station admin
              shell rather than as standalone /admin/publishers* pages.
              "Onboard channel" is a top-level route, not nested under the
              shell, so clicking it leaves the station context. */}
          {isSuperAdmin && (
            <>
              <div className="pt-3 pb-1 px-3 text-[10px] uppercase tracking-wider text-slate-500">
                Super admin
              </div>
              <NavLink
                to={`/admin/${config.slug}/channel-settings`}
                className={({ isActive }) => navLinkClass(isActive)}
              >
                <Settings className="w-4 h-4" />
                Channel settings
              </NavLink>
              <NavLink to="/admin/onboard" className={({ isActive }) => navLinkClass(isActive)}>
                <Sparkles className="w-4 h-4" />
                Onboard channel
              </NavLink>
              <NavLink to="/admin/publica-push-jobs" className={({ isActive }) => navLinkClass(isActive)}>
                <AlertTriangle className="w-4 h-4" />
                Publica push jobs
              </NavLink>
              <NavLink to="/admin/help-center" className={({ isActive }) => navLinkClass(isActive)}>
                <span className="relative">
                  <HelpCircle className="w-4 h-4" />
                  {adminHelpUnread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-[9px] font-bold text-white flex items-center justify-center">
                      {adminHelpUnread > 9 ? '9+' : adminHelpUnread}
                    </span>
                  )}
                </span>
                Help Center
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2 truncate">{user?.email}</div>
          <button
            type="button"
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-md"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10">{children}</main>
    </div>
  );
};

export default PublisherAdminLayout;
